"use client";

import { c as _c } from "react/compiler-runtime";
import Link from "next/link";
import Image from "next/image";
import { useParams } from "next/navigation";
import { NavigationMenuSub, NavigationMenuSubList, NavigationMenuSubTrigger, NavigationMenuSubContent, NavigationMenuSubViewport, NavigationMenuItem } from "../ui/navigation-menu";
import { GET_MENUS } from "@/graphql/queries/menus-query";
import { ChevronRightIcon, GraduationCap, ArrowRight, Award, Calendar, MapPin } from "lucide-react";
import * as React from "react";
import { getSystemIcon } from "@/lib/system-lucide-icon-map";
import { useSuspenseQuery } from "@apollo/client/react";
import { Icon } from "@/components/common/icon";
import { Badge } from "../ui/badge";
import { cn } from "@/lib/utils";
const getCourseLabel = (course: MenuInterface["courses"][number]) => {
  if (course.isPopular) return "Best Seller";
  if (course.isSkillBasedCourses) return "Recommended";
  if (course.isCertificationProgram) return "Trending";
  if (course.serviceFlags?.includes("NEW_LAUNCHED")) return "New Launch";
  return null;
};
const getCourseChipClass = (course: MenuInterface["courses"][number]) => {
  if (course.isPopular) return "text-primary bg-primary/20";
  if (course.isSkillBasedCourses) return "text-green-700 bg-green-30 dark:bg-green-950 dark:text-green-300";
  if (course.isCertificationProgram) return "text-secondary bg-secondary/20";
  if (course.serviceFlags.includes("NEW_LAUNCHED")) return "animate-bounce text-emerald-700 bg-emerald-30 dark:bg-emerald-950 dark:text-emerald-300";
  return "";
};
export const MegaMenu = () => {
  const $ = _c(20);
  const {
    data
  } = useSuspenseQuery(GET_MENUS);
  const params = useParams();
  const country = params?.country as string | undefined;
  let t0;
  if ($[0] !== data?.menuQuery?.menus) {
    t0 = data?.menuQuery?.menus || [];
    $[0] = data?.menuQuery?.menus;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const menus = t0;
  const firstSlug = menus[0]?.slug || null;
  const [activeTab, setActiveTab] = React.useState(firstSlug);
  let t1;
  if ($[2] !== activeTab || $[3] !== firstSlug || $[4] !== menus) {
    t1 = activeTab && menus.some(menu => menu.slug === activeTab) ? activeTab : firstSlug;
    $[2] = activeTab;
    $[3] = firstSlug;
    $[4] = menus;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  const activeTabValue = t1;
  if (!data || !data.menuQuery || !data.menuQuery.menus || menus.length === 0) {
    return null;
  }
  const t2 = activeTabValue ?? undefined;
  let t3;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <div className="text-muted-foreground/70 mb-2 px-3 text-[10px] font-bold tracking-wider uppercase">Browse Categories</div>;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== country || $[8] !== menus) {
    let t5;
    if ($[10] !== country) {
      t5 = menu_0 => {
        const hasIcon = !!menu_0.icon;
        return <NavigationMenuItem key={menu_0.slug} value={menu_0.slug} className="w-full"><NavigationMenuSubTrigger className="group hover:bg-muted/80 focus-visible:bg-muted data-open:bg-primary/8 data-open:text-primary data-popup-open:bg-primary/8 data-popup-open:text-primary data-[state=open]:bg-primary/8 data-[state=open]:text-primary relative flex w-full cursor-pointer items-center justify-between gap-3 rounded-lg py-3 pr-2.5 pl-3.5 text-left text-sm font-medium transition-all duration-200 outline-none select-none" render={<Link href={country ? `/${country}/topic/${menu_0.slug}` : `/topic/${menu_0.slug}`} />} nativeButton={false}><div className="bg-primary absolute top-2.5 bottom-2.5 left-1 w-1 rounded-full opacity-0 transition-opacity duration-200 group-data-open:opacity-100 group-data-popup-open:opacity-100 group-data-[state=open]:opacity-100" /><div className="flex min-w-0 items-center gap-3"><div className="border-border bg-background text-primary group-data-open:border-primary/20 group-data-open:bg-primary/10 group-data-open:text-primary group-data-popup-open:border-primary/20 group-data-popup-open:bg-primary/10 group-data-popup-open:text-primary group-data-[state=open]:border-primary/20 group-data-[state=open]:bg-primary/10 group-data-[state=open]:text-primary flex size-8 shrink-0 items-center justify-center rounded-lg border transition-colors duration-250">{hasIcon ? <Icon name={getSystemIcon(menu_0.icon)} className="size-4" /> : <GraduationCap className="size-4" />}</div><span className="truncate">{menu_0.linkTitle || menu_0.name}</span></div><ChevronRightIcon className="size-4 shrink-0 opacity-30 transition-all duration-200 group-hover:translate-x-0.5 group-hover:opacity-100 group-data-open:opacity-100 group-data-popup-open:opacity-100 group-data-[state=open]:opacity-100" /></NavigationMenuSubTrigger><NavigationMenuSubContent keepMounted={true}><div className="border-border flex items-center justify-between border-b pb-4"><div className="flex min-w-0 flex-col gap-1.5"><h3 className="text-foreground flex items-center gap-2 text-base font-bold tracking-tight">{menu_0.name}</h3><p className="text-muted-foreground text-xs leading-none">Acquire globally recognized skills with our certification programs.</p></div><Link href={country ? `/${country}/topic/${menu_0.slug}` : `/topic/${menu_0.slug}`} className="group/all text-primary ml-4 inline-flex shrink-0 items-center gap-1.5 text-xs font-semibold transition-all hover:underline"><span>View all</span><ArrowRight className="size-3.5 transition-transform group-hover/all:translate-x-0.5" /></Link></div><div className="grid grid-cols-2 gap-3.5">{menu_0.courses?.map(course => {
                const courseHref = country && !course.isGenericPageOnly ? `/${country}/course/${course.slug}` : `/course/${course.slug}`;
                return <Link key={course.slug} href={courseHref} className="group/course border-border/50 hover:border-primary/20 hover:bg-primary/8 hover:shadow-primary/5 dark:hover:bg-primary/4 flex items-start gap-4 rounded-xl border p-3.5 shadow-sm transition-all duration-300 hover:shadow-md"><div className="border-border bg-muted/50 group-hover/course:border-primary/30 dark:bg-muted/30 flex size-11 shrink-0 items-center justify-center rounded-xl border transition-all duration-300 group-hover/course:bg-white">{course.icon_url ? <Image src={course.icon_url} alt={course.name} width={28} height={28} className="size-7 rounded-full object-contain transition-transform duration-300 group-hover/course:scale-105" /> : <GraduationCap className="text-muted-foreground group-hover/course:text-primary size-6 transition-colors duration-300" />}</div><div className="flex min-w-0 flex-1 flex-col gap-1"><div className="flex min-w-0 items-center gap-2"><span className="text-foreground group-hover/course:text-primary truncate text-sm leading-snug font-semibold transition-colors duration-250">{course.linkTitle || course.name}</span></div><div className="text-muted-foreground/80 relative mt-1 flex flex-col gap-x-2 gap-y-1 text-[10px] font-medium">{course.modeOfTraining && <span className="flex shrink-0 items-center gap-1"><MapPin className="text-muted-foreground/60 size-3" />{course.modeOfTraining}</span>}<span className="flex gap-x-2">{course.serviceTrainingDays && <span className="flex shrink-0 items-center gap-1"><Calendar className="text-muted-foreground/60 size-3" />{course.serviceTrainingDays}</span>}{course.accreditations && course.accreditations.length > 0 && <><span className="text-muted-foreground/30 shrink-0">•</span><span className="flex max-w-130 items-center gap-1 truncate"><Award className="text-muted-foreground/60 size-3" />{course.accreditations.map(_temp).join(", ")}</span></>}{getCourseLabel(course) && <Badge className={cn("absolute -right-2 -bottom-2", getCourseChipClass(course))}>{getCourseLabel(course)}</Badge>}</span></div></div></Link>;
              })}</div></NavigationMenuSubContent></NavigationMenuItem>;
      };
      $[10] = country;
      $[11] = t5;
    } else {
      t5 = $[11];
    }
    t4 = menus.map(t5);
    $[7] = country;
    $[8] = menus;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[12] !== t4) {
    t5 = <NavigationMenuSubList className="border-border bg-muted/20 flex max-h-163 w-fit shrink-0 flex-col gap-1 overflow-y-auto border-r p-3.5">{t3}{t4}</NavigationMenuSubList>;
    $[12] = t4;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  let t6;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <NavigationMenuSubViewport className="max-h-163" />;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] !== t5) {
    t7 = <div className="flex h-auto w-full">{t5}{t6}</div>;
    $[15] = t5;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] !== t2 || $[18] !== t7) {
    t8 = <NavigationMenuSub value={t2} onValueChange={setActiveTab} orientation="vertical" className="flex w-7xl bg-transparent shadow-2xl">{t7}</NavigationMenuSub>;
    $[17] = t2;
    $[18] = t7;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  return t8;
};
function _temp(accreditation) {
  return accreditation.name;
}